import { z } from 'zod';

/**
 * Le procedure dell'azienda (ADR 0214): composte dalla libreria dell'ADR 0209 nella Fase 0, dopo i ruoli, con lo stato
 * e il motivo, chi la svolge e chi ne risponde, la cadenza, i passi e la prova. Il titolare le attiva.
 */
export const COMPANY_PROCEDURE_STATUSES = ['vale', 'da_confermare', 'esclusa'] as const;
export type CompanyProcedureStatus = (typeof COMPANY_PROCEDURE_STATUSES)[number];

/** Un ruolo della procedura, con chi lo ricopre. */
export interface CompanyProcedureRoleDto {
  chiave: string;
  nome: string;
  /** Le persone assegnate e chi lo ricopre secondo la visura; vuoto = da assegnare. */
  persone: string[];
  di_solito: 'interno' | 'esterno' | 'entrambi' | null;
  esterno_tipico: string | null;
  /** Il ruolo che di solito lo copre a questa dimensione (nome), o «uno dei lavoratori». */
  coperto_da: string | null;
}

export interface CompanyProcedureDto {
  id: string;
  chiave: string;
  nome: string;
  descrizione: string | null;
  area: string;
  genere: string | null;
  /** La cadenza in parole: «ogni mese, entro il 16». */
  cadenza: string;
  nota_cadenza: string | null;
  /** azienda (una volta), sede (in questa sede) o per (per ogni dato, `per`). */
  dove: 'azienda' | 'sede' | 'per';
  per: string | null;
  sede_id: string | null;
  sede: string | null;
  stato: CompanyProcedureStatus;
  motivo: string | null;
  condizione: string | null;
  ne_risponde: CompanyProcedureRoleDto;
  la_svolge: CompanyProcedureRoleDto;
  consultati: string[];
  informati: string[];
  passi: Array<{ nome: string; chi: string | null }>;
  prova: string;
  adempimenti: string[];
  norma: string | null;
  base: 'legge' | 'prassi' | null;
  /** «base» o il gruppo ATECO. */
  strato: string;
  attiva: boolean;
  attivata_il: string | null;
  attivata_da: string | null;
  modificata_dal_titolare: boolean;
  /** Formato 2 (ADR 0215 tappa 4): da cosa parte, in parole. */
  avvio: {
    tempo: string | null;
    esterni: Array<{ da: string; evento: string }>;
    dopo: Array<{ procedura: string; se: string | null }>;
    entro: string | null;
  };
  /** Le entità che legge e quelle che crea, modifica o chiude (nomi in parole). */
  legge: string[];
  scrive: Array<{ entita: string; azione: 'crea' | 'modifica' | 'chiude' }>;
  standard: CompanyStandardDto[];
  /** Il ruolo che approva (nome), se serve. */
  approva: string | null;
  /** I generi di programma che di solito la svolgono, e i programmi dell'azienda di quel genere («lo può svolgere»). */
  sistemi: string[];
  lo_puo_svolgere: string[];
  /** I compiti aperti, per una procedura attiva. */
  compiti_aperti: ProcedureTaskDto[];
}

/** Uno standard dell'azienda (ADR 0215 § 4): il valore atteso, «da fissare» se manca e non viene dal contratto. */
export interface CompanyStandardDto {
  id: string;
  chiave: string;
  nome: string;
  misura: string;
  valore: number | string | null;
  unita: string | null;
  confronto: 'al_massimo' | 'almeno' | 'entro' | 'uguale' | null;
  base: 'legge' | 'contratto' | 'prassi' | 'azienda';
  fonte: string | null;
  norma: string | null;
  /** Con base contratto: il campo del dato che dà il valore. */
  da: { entita: string; campo: string } | null;
  da_fissare: boolean;
  /** Un responsabile può fissarne il valore (base azienda o prassi). */
  fissabile: boolean;
  fissato_da: string | null;
  fissato_il: string | null;
  strato: string;
  /**
   * Come si misura dai dati (ADR 0215 tappa 5): con una verifica sulle voci (il controllo di gestione dell'ADR 0018) o
   * con un indicatore (ADR 0213); null se la libreria non lo dice. `manca` dice perché ancora non si misura.
   */
  dai_dati: { come: 'verifica' | 'indicatore' | null; manca: string | null; controllo_id: string | null } | null;
}

/** La misura di uno standard dai dati, per un compito di controllo (ADR 0215 tappa 5). */
export interface ProcedureTaskMeasure {
  standard: string;
  come: 'verifica' | 'indicatore' | null;
  /** Che cosa si è contato, in parole. */
  spiegazione: string | null;
  periodo: { inizio: string; fine: string } | null;
  /** Le voci fuori standard (verifica) o il valore dell'indicatore; null se non si misura. */
  valore: number | null;
  atteso: number | string | null;
  esito: 'in_standard' | 'fuori_standard' | null;
  /** Con la verifica: le voci controllate, quelle fuori standard e quelle senza i dati per dirlo. */
  voci: { controllate: number; fuori: number; non_verificabili: number; esempi: Array<{ rif: string; motivo: string }> } | null;
  manca: string | null;
}

export const PROCEDURE_TASK_SOURCES = ['tempo', 'esterno', 'dopo', 'controllo'] as const;
export const PROCEDURE_TASK_SCOPES = ['mine', 'open', 'unassigned'] as const;
export type ProcedureTaskScope = (typeof PROCEDURE_TASK_SCOPES)[number];

/** Un compito da una procedura attiva (ADR 0215 tappa 4). */
export interface ProcedureTaskDto {
  id: string;
  procedura_id: string;
  procedura: string;
  genere: string | null;
  sede_id: string | null;
  sede: string | null;
  /** tempo, esterno, dopo (un'altra procedura chiusa), controllo (un controllo fuori standard). */
  origine: (typeof PROCEDURE_TASK_SOURCES)[number];
  /** Il trigger in parole: «ogni mese», «è arrivato: …», «dopo «…»». */
  trigger: string;
  /** La condizione del `dopo` a parole: se non vale, il compito si annulla. */
  se: string | null;
  nota_apertura: string | null;
  periodo: { inizio: string; fine: string } | null;
  /** L'entità su cui si ripete e il dato, se si conosce. */
  entita: string | null;
  dato: string | null;
  scadenza: string | null;
  scaduto: boolean;
  ruolo: { chiave: string; nome: string };
  approva: string | null;
  persone: string[];
  da_assegnare: boolean;
  mio: boolean;
  puo_chiudere: boolean;
  stato: 'aperta' | 'chiusa' | 'annullata';
  esito: 'fatto' | 'in_standard' | 'fuori_standard' | null;
  /** Per un controllo: gli standard da confrontare. */
  standard: CompanyStandardDto[];
  standard_usato: string | null;
  valore_atteso: number | string | null;
  valore_misurato: number | string | null;
  prova_attesa: string;
  prova: string | null;
  passi: Array<{ nome: string; chi: string | null }>;
  lo_puo_svolgere: string[];
  nota: string | null;
  aperto_il: string;
  chiuso_da: string | null;
  chiuso_il: string | null;
}

/** La chiusura di un compito: l'esito (per un controllo che non si confronta da solo), il valore misurato, la nota. */
export const ProcedureTaskCloseSchema = z.object({
  esito: z.enum(['fatto', 'in_standard', 'fuori_standard']).optional(),
  valore_misurato: z.union([z.number(), z.string().trim().max(200)]).nullable().optional(),
  /** Con uno standard dal contratto: il valore promesso nel dato. */
  valore_atteso: z.number().nullable().optional(),
  standard: z.string().trim().max(200).optional(),
  nota: z.string().trim().max(4000).optional(),
  /** Riferimento facoltativo alla prova in archivio (id del documento). */
  prova: z.string().trim().max(200).optional(),
});
export type ProcedureTaskClose = z.infer<typeof ProcedureTaskCloseSchema>;

export const ProcedureTaskCancelSchema = z.object({ nota: z.string().trim().max(4000).optional() });

/** «È arrivato…»: apre a mano il compito di una procedura con un trigger esterno. */
export const ProcedureArrivedSchema = z.object({
  procedura_id: z.string().uuid(),
  evento: z.string().trim().max(300).optional(),
  dato: z.string().trim().max(300).optional(),
  nota: z.string().trim().max(2000).optional(),
});
export type ProcedureArrived = z.infer<typeof ProcedureArrivedSchema>;

export interface ProcedureTaskCloseResult {
  compito: ProcedureTaskDto;
  /** I compiti aperti dalla chiusura: la catena dei `dopo` e le correzioni di un controllo fuori standard. */
  aperti: ProcedureTaskDto[];
}

/** Un responsabile fissa il valore di uno standard, con il confronto se lo standard non lo dice. */
export const StandardValueSchema = z.object({
  valore: z.union([z.number(), z.string().trim().min(1).max(200)]),
  unita: z.string().trim().max(60).nullable().optional(),
  confronto: z.enum(['al_massimo', 'almeno', 'entro', 'uguale']).nullable().optional(),
});
export type StandardValue = z.infer<typeof StandardValueSchema>;

export interface CompanyProceduresState {
  aree: Array<{ chiave: string; nome: string }>;
  procedure: CompanyProcedureDto[];
  vale: number;
  da_confermare: number;
  esclusa: number;
  attive: number;
}

/** Il titolare attiva o disattiva una procedura. */
export const ProcedureActivationSchema = z.object({ attiva: z.boolean() });
export type ProcedureActivation = z.infer<typeof ProcedureActivationSchema>;

/**
 * Il titolare attiva tutte le procedure che valgono in un'area, nel livello che vede: una sede (id), «azienda» (quelle
 * dell'azienda e per dato) o tutte, senza livello.
 */
export const ProcedureAreaActivationSchema = z.object({
  area: z.string().trim().min(1).max(80),
  livello: z.union([z.string().uuid(), z.literal('azienda')]).nullable().optional(),
});
export type ProcedureAreaActivation = z.infer<typeof ProcedureAreaActivationSchema>;
